import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { startOfDay, endOfDay, subDays } from "date-fns";
import {
  BarChart3,
  Package,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  Truck,
  Activity,
  DollarSign,
  RefreshCw,
  FileText,
  Undo2,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shared/components/ui/tabs";
import { DatePickerWithRange } from "@shared/components/date-picker-with-range";
import { SelectCity } from "@/features/settings/citys/SelectCity";
import { useIsMobile } from "@shared/hooks/use-mobile";
import HeadLayout from "@shared/layouts/HeadLayout";
import useTitle from "@shared/hooks/useTitle";

// Import services and hooks
import { getAllLivreurs } from "@/features/packages/services/packagesService";
import { fetchAllUsers } from "@features/users/userService";
import { useOverview } from "@features/dashboard/hooks/useStatstics";
import PackagesStatusTable from "@features/dashboard/components/PackagesStatusTable";
import InvoiceStatusTable from "@features/dashboard/components/InvoiceStatusTable";
import StatCard from "../components/StatCard";
import PackageCard from "../components/PackageCard";
import { DashboardCardsSkeleton } from "@features/dashboard/components/dashboard-cards";

export default function ManagerDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("overview");

  useTitle(t("Manager Dashboard"));

  // Date range state
  const [params, setParams] = useState({
    from: startOfDay(subDays(new Date(), 30)), // Last 30 days
    to: endOfDay(new Date()),
  });

  // Fetch dashboard overview data with date filtering
  const {
    data: stats,
    isLoading: statsLoading,
    refetch: refetchStats,
    isFetching: statsFetching,
  } = useOverview(params, {
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch livreurs data
  const {
    data: livreursData,
    isLoading: livreursLoading,
    refetch: refetchLivreurs,
  } = useQuery({
    queryKey: ["manager-livreurs"],
    queryFn: getAllLivreurs,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch users data
  const {
    data: usersData,
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ["manager-users"],
    queryFn: () => fetchAllUsers({ page: 1, limit: 1000 }),
    staleTime: 5 * 60 * 1000,
  });

  const livreurs = livreursData?.livreurs || [];
  const users = usersData?.users || [];

  // Calculate manager-specific metrics with real data from stats
  const managerMetrics = {
    // Packages
    totalPackages: stats?.packages?.totalPackagesInRange || 0,
    deliveredPackages: stats?.packages?.totalDelivered || 0,
    pendingPackages: stats?.packages?.totalPending || 0,
    inTransitPackages: stats?.packages?.totalInTransit || 0,
    returnedPackages: stats?.packages?.totalReturned || 0,

    // Amounts
    totalRevenue: stats?.amounts?.totalRevenue || 0,
    totalProfit: stats?.amounts?.profit || 0,
    deliveryFees: stats?.amounts?.deliveryFees || 0,

    // Livreurs
    totalLivreurs: livreurs.length,
    activeLivreurs: livreurs.filter((l) => l.isActive).length,
    availableLivreurs: livreurs.filter(
      (l) => l.isActive && !l.currentAssignments?.length
    ).length,

    // Users/Clients
    totalUsers: users.length,
    activeUsers: users.filter((u) => u.isActive).length,
    totalClients: users.filter((u) => u.role === "client").length,
    activeClients: users.filter((u) => u.role === "client" && u.isActive)
      .length,

    // Invoices
    totalInvoices: stats?.invoices?.totalInvoices || 0,
    paidInvoices: stats?.invoices?.totalPaid || 0,
    pendingInvoices: stats?.invoices?.totalPending || 0,

    // Pickups
    totalPickups: stats?.pickups?.totalPickups || 0,
    completedPickups: stats?.pickups?.totalCompleted || 0,
    pendingPickups: stats?.pickups?.totalPending || 0,

    // Return Vouchers
    totalReturnVouchers: stats?.returnVouchers?.totalReturnVouchers || 0,
    paidReturnVouchers: stats?.returnVouchers?.totalPaid || 0,
  };

  // Package status data for charts
  const packageStatusData = stats?.packageStatus || [];
  const recentPackages = (
    stats?.recentPackages ||
    stats?.lastPackages ||
    []
  ).filter((pkg) => pkg && pkg.customer && pkg.trackingNumber);

  // Handle date range change
  const handleDateRangeChange = (newDateRange) => {
    if (!newDateRange?.from || !newDateRange?.to) {
      return;
    }
    setParams({
      ...params,
      from: startOfDay(newDateRange.from),
      to: endOfDay(newDateRange.to),
    });
  };

  // Handle city filter
  const handleCityChange = (city) => {
    setParams((prev) => ({
      ...prev,
      city: city?.name || undefined,
    }));
  };

  // Handle refresh all
  const handleRefreshAll = async () => {
    await Promise.all([refetchStats(), refetchLivreurs(), refetchUsers()]);
  };

  const isLoading = statsLoading || livreursLoading || usersLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <DashboardCardsSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <HeadLayout title={t("Manager Dashboard")}>
        <div className="flex items-center gap-2">
          <DatePickerWithRange
            initialDate={{ from: params.from, to: params.to }}
            onSelectDate={handleDateRangeChange}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAll}
            disabled={statsFetching}
            className="flex items-center gap-2"
          >
            <RefreshCw
              className={`h-4 w-4 ${statsFetching ? "animate-spin" : ""}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
        </div>
      </HeadLayout>

      {/* City Filter */}
      <div className="flex items-center gap-2">
        <SelectCity onSelectCity={handleCityChange} />
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Packages */}
        <StatCard
          title={t("Total Packages")}
          value={managerMetrics.totalPackages.toLocaleString()}
          icon={Package}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
          trend={{
            value: `${managerMetrics.deliveredPackages} ${t("delivered")}`,
            isPositive: true,
          }}
        />

        {/* Total Revenue */}
        <StatCard
          title={t("Total Revenue")}
          value={`${managerMetrics.totalRevenue.toLocaleString()} MAD`}
          icon={DollarSign}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          trend={{
            value: `${managerMetrics.totalProfit.toLocaleString()} MAD ${t(
              "profit"
            )}`,
            isPositive: true,
          }}
        />

        {/* Total Livreurs */}
        <StatCard
          title={t("Total Livreurs")}
          value={managerMetrics.totalLivreurs.toLocaleString()}
          icon={Truck}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
          trend={{
            value: `${managerMetrics.activeLivreurs} ${t("active")}`,
            isPositive: true,
          }}
        />

        {/* Total Clients */}
        <StatCard
          title={t("Total Clients")}
          value={managerMetrics.totalClients.toLocaleString()}
          icon={Users}
          iconBgColor="bg-orange-100"
          iconColor="text-orange-600"
          trend={{
            value: `${managerMetrics.activeClients} ${t("active")}`,
            isPositive: true,
          }}
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Invoices */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Total Invoices")}
                </p>
                <p className="text-2xl font-bold">
                  {managerMetrics.totalInvoices}
                </p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="flex items-center mt-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-green-600">
                {managerMetrics.paidInvoices} {t("paid")}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Pickups */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Total Pickups")}
                </p>
                <p className="text-2xl font-bold">
                  {managerMetrics.totalPickups}
                </p>
              </div>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Truck className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
            <div className="flex items-center mt-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-green-600">
                {managerMetrics.completedPickups} {t("completed")}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Return Vouchers */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("Return Vouchers")}
                </p>
                <p className="text-2xl font-bold">
                  {managerMetrics.totalReturnVouchers}
                </p>
              </div>
              <div className="p-2 bg-red-100 rounded-lg">
                <Undo2 className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <div className="flex items-center mt-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-green-600">
                {managerMetrics.paidReturnVouchers} {t("paid")}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* In Transit */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("In Transit")}
                </p>
                <p className="text-2xl font-bold">
                  {managerMetrics.inTransitPackages}
                </p>
              </div>
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <div className="flex items-center mt-2 text-sm">
              <Activity className="h-4 w-4 text-blue-500 mr-1" />
              <span className="text-blue-600">
                {managerMetrics.pendingPackages} {t("pending")}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Management Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">{t("Overview")}</TabsTrigger>
          <TabsTrigger value="packages">{t("Packages")}</TabsTrigger>
          <TabsTrigger value="operations">{t("Operations")}</TabsTrigger>
          <TabsTrigger value="analytics">{t("Analytics")}</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Package Status Table */}
            <PackagesStatusTable packageStatus={packageStatusData} />

            {/* Invoice Status */}
            <InvoiceStatusTable />
          </div>

          {/* Recent Packages */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                {t("Recent Packages")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentPackages.length > 0 ? (
                <div className="space-y-3">
                  {recentPackages.slice(0, 5).map((pkg) => (
                    <PackageCard key={pkg._id} pkg={pkg} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("No recent packages")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Packages Tab */}
        <TabsContent value="packages" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Package Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle>{t("Package Management")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium">
                    {t("Total Packages")}
                  </span>
                  <Badge variant="secondary">
                    {managerMetrics.totalPackages}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium">{t("Delivered")}</span>
                  <Badge
                    variant="default"
                    className="bg-green-100 text-green-800"
                  >
                    {managerMetrics.deliveredPackages}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium">{t("In Transit")}</span>
                  <Badge variant="outline">
                    {managerMetrics.inTransitPackages}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium">{t("Returned")}</span>
                  <Badge variant="destructive">
                    {managerMetrics.returnedPackages}
                  </Badge>
                </div>
                <Button
                  className="w-full"
                  onClick={() => navigate("/packages")}
                >
                  {t("Manage Packages")}
                </Button>
              </CardContent>
            </Card>

            {/* Livreur Management */}
            <Card>
              <CardHeader>
                <CardTitle>{t("Livreur Management")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium">
                    {t("Total Livreurs")}
                  </span>
                  <Badge variant="secondary">
                    {managerMetrics.totalLivreurs}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium">{t("Active")}</span>
                  <Badge variant="default">
                    {managerMetrics.activeLivreurs}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium">{t("Available")}</span>
                  <Badge variant="outline">
                    {managerMetrics.availableLivreurs}
                  </Badge>
                </div>
                <Button
                  className="w-full"
                  onClick={() => navigate("/packages/livreur-management")}
                >
                  {t("Manage Livreurs")}
                </Button>
              </CardContent>
            </Card>

            {/* User Management */}
            <Card>
              <CardHeader>
                <CardTitle>{t("User Management")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium">
                    {t("Total Users")}
                  </span>
                  <Badge variant="secondary">{managerMetrics.totalUsers}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium">{t("Active")}</span>
                  <Badge variant="default">{managerMetrics.activeUsers}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium">{t("Clients")}</span>
                  <Badge variant="outline">
                    {users.filter((u) => u.role === "client").length}
                  </Badge>
                </div>
                <Button className="w-full" asChild>
                  <a href="/users">{t("Manage Users")}</a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Operations Tab */}
        <TabsContent value="operations" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* System Status */}
            <Card>
              <CardHeader>
                <CardTitle>{t("System Status")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t("Package Processing")}</span>
                  <Badge
                    variant="default"
                    className="bg-green-100 text-green-800"
                  >
                    {t("Normal")}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t("Delivery Network")}</span>
                  <Badge
                    variant="default"
                    className="bg-green-100 text-green-800"
                  >
                    {t("Operational")}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t("User Activity")}</span>
                  <Badge
                    variant="default"
                    className="bg-blue-100 text-blue-800"
                  >
                    {t("High")}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Performance Metrics */}
            <Card>
              <CardHeader>
                <CardTitle>{t("Performance")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t("Delivery Rate")}</span>
                  <span className="text-sm font-medium text-green-600">
                    94%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t("Customer Satisfaction")}</span>
                  <span className="text-sm font-medium text-green-600">
                    4.8/5
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t("On-Time Delivery")}</span>
                  <span className="text-sm font-medium text-blue-600">89%</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  {t("Monthly Revenue Trend")}
                </CardTitle>
              </CardHeader>
              <CardContent>{/* <MonthlyRevenueChart /> */}</CardContent>
            </Card>

            {/* Performance Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  {t("Key Performance Indicators")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t("Average Delivery Time")}</span>
                    <span className="font-medium">2.3 days</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: "76%" }}
                    ></div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t("Customer Retention Rate")}</span>
                    <span className="font-medium">87%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: "87%" }}
                    ></div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t("Operational Efficiency")}</span>
                    <span className="font-medium">92%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full"
                      style={{ width: "92%" }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
